export interface Vec2 {
  readonly x: number;
  readonly y: number;
}

export const vec = (x: number, y: number): Vec2 => ({ x, y });

export const add = (a: Vec2, b: Vec2): Vec2 => vec(a.x + b.x, a.y + b.y);
export const sub = (a: Vec2, b: Vec2): Vec2 => vec(a.x - b.x, a.y - b.y);
export const scale = (a: Vec2, k: number): Vec2 => vec(a.x * k, a.y * k);
export const negate = (a: Vec2): Vec2 => vec(-a.x, -a.y);
export const dot = (a: Vec2, b: Vec2): number => a.x * b.x + a.y * b.y;
export const cross = (a: Vec2, b: Vec2): number => a.x * b.y - a.y * b.x;
export const lenSq = (a: Vec2): number => a.x * a.x + a.y * a.y;
export const len = (a: Vec2): number => Math.hypot(a.x, a.y);
export const distSq = (a: Vec2, b: Vec2): number => lenSq(sub(a, b));
export const dist = (a: Vec2, b: Vec2): number => len(sub(a, b));
export const perp = (a: Vec2): Vec2 => vec(-a.y, a.x);
export const lerp = (a: Vec2, b: Vec2, t: number): Vec2 => add(a, scale(sub(b, a), t));
export const equals = (a: Vec2, b: Vec2, eps = 1e-9): boolean => distSq(a, b) <= eps * eps;

export function normalize(a: Vec2): Vec2 {
  const l = len(a);
  return l === 0 ? vec(0, 0) : scale(a, 1 / l);
}

export function rotate(a: Vec2, radians: number): Vec2 {
  const c = Math.cos(radians);
  const s = Math.sin(radians);
  return vec(a.x * c - a.y * s, a.x * s + a.y * c);
}

export const DEG = Math.PI / 180;

/** `p` turned `degrees` counter-clockwise about `center`. */
export const rotateAbout = (p: Vec2, center: Vec2, degrees: number): Vec2 =>
  add(center, rotate(sub(p, center), degrees * DEG));

/** `p` reflected across the line through `a` and `b`; `p` itself when they coincide. */
export function reflectAcross(p: Vec2, a: Vec2, b: Vec2): Vec2 {
  const along = sub(b, a);
  const length = lenSq(along);
  if (length === 0) return p;
  const foot = add(a, scale(along, dot(sub(p, a), along) / length));
  return sub(scale(foot, 2), p);
}

export const toRadians = (degrees: number): number => degrees * DEG;
export const toDegrees = (radians: number): number => radians / DEG;

export function normalizeDegrees(degrees: number): number {
  const d = degrees % 360;
  return d < 0 ? d + 360 : d;
}

export const angleOf = (a: Vec2): number => normalizeDegrees(toDegrees(Math.atan2(a.y, a.x)));

export const fromPolar = (center: Vec2, radius: number, degrees: number): Vec2 =>
  vec(
    center.x + radius * Math.cos(toRadians(degrees)),
    center.y + radius * Math.sin(toRadians(degrees)),
  );
